import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "../lib/api.js";
import type { ModelCatalogEntry, ModelRef, ModelTask } from "../lib/types.js";
import { Modal } from "../components/Modal.js";
import { SPEED_LABEL, COST_LABEL, getRememberedModel, rememberModel } from "./catalog.js";

const TASK_TITLE: Partial<Record<ModelTask, string>> = {
  extraction: "Auto-populate from book",
  continuity: "Continuity analysis",
  critique: "Story critique",
  summary: "Summarize",
  rewrite: "Rewrite",
  "character-analysis": "Relationship detection",
  "general-chat": "Chat"
};

export function ModelRecommendationBadge({ kind }: { kind: "recommended" | "deep" | "fast" | "alt" }) {
  const label = kind === "recommended" ? "Recommended" : kind === "deep" ? "Deep analysis" : kind === "fast" ? "Fast alternative" : "Alternative";
  const bg = kind === "recommended" ? "var(--accent, #2bb6a3)" : "var(--surface-3, rgba(127,127,127,0.18))";
  const fg = kind === "recommended" ? "#07201d" : "var(--muted, #888)";
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function badgeKind(m: ModelCatalogEntry): "recommended" | "deep" | "fast" | "alt" {
  if (m.recommended) return "recommended";
  if (m.cost === "higher" || m.speed === "slow") return "deep";
  if (m.speed === "fast" || m.speed === "very-fast") return "fast";
  return "alt";
}

function ModelCard({ m, selected, onSelect }: { m: ModelCatalogEntry; selected: boolean; onSelect: () => void }) {
  const disabled = !m.available;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      style={{
        display: "block", width: "100%", textAlign: "left", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1, padding: "10px 12px", borderRadius: 10, marginBottom: 8,
        border: `1.5px solid ${selected ? "var(--accent, #2bb6a3)" : "var(--border, rgba(127,127,127,0.25))"}`,
        background: selected ? "var(--accent-soft, rgba(43,182,163,0.1))" : "var(--surface-2, rgba(127,127,127,0.05))"
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{m.displayName}</span>
        {disabled ? <ModelRecommendationBadge kind="alt" /> : <ModelRecommendationBadge kind={badgeKind(m)} />}
      </div>
      <div style={{ fontSize: 11, color: "var(--muted, #888)", margin: "2px 0 4px" }}>
        {m.provider} · {SPEED_LABEL[m.speed]} · {COST_LABEL[m.cost]}
      </div>
      <div style={{ fontSize: 12 }}>
        {disabled
          ? (m.disabledReason ?? "Not available.")
          : m.recommended
            ? (m.recommendationReason || m.description)
            : m.description}
      </div>
    </button>
  );
}

/** The modal body: fetches the catalog for the task and lets the user pick. */
function PickerModal({
  projectId, task, onPick, onCancel
}: { projectId: string; task: ModelTask; onPick: (ref: ModelRef, remember: boolean) => void; onCancel: () => void }) {
  const [models, setModels] = useState<ModelCatalogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ModelRef | null>(null);
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.aiModels(projectId, task)
      .then((r) => {
        if (cancelled) return;
        setModels(r.models);
        const rec = r.models.find((m) => m.recommended && m.available) ?? r.models.find((m) => m.available) ?? null;
        setSelected(rec ? { provider: rec.provider, model: rec.model } : null);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : "Could not load models."));
    return () => { cancelled = true; };
  }, [projectId, task]);

  const recommended = (models ?? []).filter((m) => m.recommended);
  const others = (models ?? []).filter((m) => !m.recommended);
  const isSel = (m: ModelCatalogEntry) => !!selected && selected.provider === m.provider && selected.model === m.model;

  return (
    <Modal
      title="Choose AI Model"
      subtitle={`What do you want to use for ${TASK_TITLE[task] ?? "this"}?`}
      onClose={onCancel}
      width={560}
      footer={
        <>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginRight: "auto" }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Remember my choice for this action
          </label>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button
            type="button" className="btn btn-primary"
            disabled={!selected}
            onClick={() => selected && onPick(selected, remember)}
          >
            Continue
          </button>
        </>
      }
    >
      {error && <p className="faint" style={{ color: "var(--danger, #c0392b)" }}>{error}</p>}
      {!models && !error && <p className="faint">Loading models…</p>}
      {models && models.length === 0 && (
        <p className="faint">No AI providers are configured on the server. Set a provider key and redeploy.</p>
      )}
      {recommended.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted,#888)", margin: "0 0 6px" }}>RECOMMENDED FOR THIS TASK</div>
          {recommended.map((m) => <ModelCard key={`${m.provider}:${m.model}`} m={m} selected={isSel(m)} onSelect={() => setSelected({ provider: m.provider, model: m.model })} />)}
        </>
      )}
      {others.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted,#888)", margin: "10px 0 6px" }}>OTHER AVAILABLE MODELS</div>
          {others.map((m) => <ModelCard key={`${m.provider}:${m.model}`} m={m} selected={isSel(m)} onSelect={() => setSelected({ provider: m.provider, model: m.model })} />)}
        </>
      )}
    </Modal>
  );
}

// ---- Context: one modal mount, pickModel() from anywhere ----
interface PickRequest { task: ModelTask; resolve: (ref: ModelRef | null) => void; }
interface ModelPickerApi {
  /** Resolve the model for a task: returns a remembered choice immediately, else
   * opens the picker and resolves on Continue (null on Cancel). */
  pickModel: (opts: { task: ModelTask }) => Promise<ModelRef | null>;
}
const Ctx = createContext<ModelPickerApi | null>(null);

export function ModelPickerProvider({ projectId, children }: { projectId: string; children: ReactNode }) {
  const [pending, setPending] = useState<PickRequest | null>(null);
  const projectRef = useRef(projectId);
  projectRef.current = projectId;

  const pickModel = useCallback(({ task }: { task: ModelTask }) => {
    const remembered = getRememberedModel(task, projectRef.current);
    if (remembered) return Promise.resolve<ModelRef | null>(remembered);
    return new Promise<ModelRef | null>((resolve) => setPending({ task, resolve }));
  }, []);

  const value = useMemo(() => ({ pickModel }), [pickModel]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {pending && (
        <PickerModal
          projectId={projectId}
          task={pending.task}
          onCancel={() => { pending.resolve(null); setPending(null); }}
          onPick={(ref, remember) => {
            if (remember) rememberModel(pending.task, projectId, ref);
            pending.resolve(ref);
            setPending(null);
          }}
        />
      )}
    </Ctx.Provider>
  );
}

export function useModelPicker(): ModelPickerApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useModelPicker must be used within a ModelPickerProvider");
  return ctx;
}
